import React from "react";

import {
  Box,
  Typography,
  Container,
  Button,
  Divider,
  Stack,
} from "@mui/material";

import { motion } from "framer-motion";

import ArrowOutwardRoundedIcon from "@mui/icons-material/ArrowOutwardRounded";
import PhoneRoundedIcon from "@mui/icons-material/PhoneRounded";
import MailOutlineRoundedIcon from "@mui/icons-material/MailOutlineRounded";
import WhatsAppIcon from "@mui/icons-material/WhatsApp";
import AccessTimeRoundedIcon from "@mui/icons-material/AccessTimeRounded";
import ArrowDownwardRoundedIcon from "@mui/icons-material/ArrowDownwardRounded";

import ContactForm from "../components/ContactForm";
import SEO from "../components/SEO";

const CONTACT_SCHEMA = {
  "@context": "https://schema.org",
  "@graph": [
    {
      "@type": "ContactPage",
      "@id": "https://studio.wiyoutech.in/contact#page",
      "name": "Contact WiYou! Tech Studios | Jaipur, Rajasthan",
      "description": "Get in touch with WiYou! Tech Studios in Jaipur, Rajasthan for web development, web design, digital menu engineering, and custom web applications.",
      "url": "https://studio.wiyoutech.in/contact",
      "isPartOf": { "@type": "WebSite", "@id": "https://studio.wiyoutech.in/#website" }
    },
    {
      "@type": "ProfessionalService",
      "@id": "https://studio.wiyoutech.in/#organization",
      "name": "WiYou! Tech Studios",
      "url": "https://studio.wiyoutech.in",
      "email": "officialwiyou.team@gmail.com",
      "telephone": "+91 7689919139",
      "address": {
        "@type": "PostalAddress",
        "addressLocality": "Jaipur",
        "addressRegion": "Rajasthan",
        "postalCode": "302001",
        "addressCountry": "IN"
      },
      "geo": {
        "@type": "GeoCoordinates",
        "latitude": "26.9124",
        "longitude": "75.7873"
      },
      "areaServed": [
        { "@type": "City", "name": "Jaipur" },
        { "@type": "State", "name": "Rajasthan" },
        { "@type": "Country", "name": "India" }
      ],
      "contactPoint": {
        "@type": "ContactPoint",
        "telephone": "+91-7689919139",
        "contactType": "customer service",
        "areaServed": ["IN"],
        "availableLanguage": ["English", "Hindi"]
      }
    }
  ]
};

const MotionBox = motion(Box);

const fadeUp = {
  hidden: {
    opacity: 0,
    y: 35,
  },
  visible: {
    opacity: 1,
    y: 0,
    transition: {
      duration: 0.7,
      ease: [0.22, 1, 0.36, 1],
    },
  },
};

const stagger = {
  hidden: {},
  visible: {
    transition: {
      staggerChildren: 0.12,
    },
  },
};

const CONTACT_EMAIL = "officialwiyou.team@gmail.com";
const PHONE_NUMBER = "+91 7689919139";
const WHATSAPP_NUMBER = "917689919139";

const Contact = () => {
  const scrollToForm = () => {
    document
      .getElementById("contact-form")
      ?.scrollIntoView({
        behavior: "smooth",
        block: "start",
      });
  };

  const contactMethods = [
    {
      icon: <PhoneRoundedIcon />,
      label: "Call us",
      value: PHONE_NUMBER,
      href: `tel:${PHONE_NUMBER.replace(/\s/g, "")}`,
    },
    {
      icon: <WhatsAppIcon />,
      label: "WhatsApp",
      value: "Start a conversation",
      href: `https://wa.me/${WHATSAPP_NUMBER}`,
    },
    {
      icon: <MailOutlineRoundedIcon />,
      label: "Email",
      value: CONTACT_EMAIL,
      href: `mailto:${CONTACT_EMAIL}`,
    },
  ];

  return (
    <Box
      sx={{
        minHeight: "100vh",
        backgroundColor: "#f7f4ef00",
        color: "#2B211C",
        pt: {
          xs: 13,
          md: 18,
        },
      }}
    >
      <SEO
        title="Contact WiYou! Tech Studios | Web Development Studio Jaipur"
        description="Connect with WiYou! Tech Studios in Jaipur, Rajasthan. Let's discuss your web development, custom web app, or digital menu project. Call +91 7689919139."
        keywords={[
          "contact WiYou Tech Studios",
          "web development company Jaipur contact",
          "website designer Jaipur phone",
          "hire web developers Jaipur",
          "digital studio Jaipur",
          "Jaipur web agency"
        ]}
        canonicalUrl="https://studio.wiyoutech.in/contact"
        schema={CONTACT_SCHEMA}
      />
      <Container
        maxWidth="xl"
        sx={{
          px: {
            xs: 2.5,
            sm: 4,
            md: 6,
          },
        }}
      >
        {/* =====================================================
            HERO
        ====================================================== */}

        <MotionBox
          variants={stagger}
          initial="hidden"
          animate="visible"
          sx={{
            maxWidth: 1180,
            mx: "auto",
            mb: {
              xs: 10,
              md: 15,
            },
          }}
        >
          <MotionBox variants={fadeUp}>
            <Typography
              component="div"
              sx={{
                display: "inline-flex",
                alignItems: "center",
                gap: 1,
                px: 1.5,
                py: 0.75,
                border: "1px solid #DCD4CC",
                borderRadius: "999px",
                backgroundColor: "#FCFAF6",
                color: "#75675E",
                fontSize: "0.7rem",
                fontWeight: 700,
                letterSpacing: "0.16em",
                textTransform: "uppercase",
              }}
            >
              Contact WiYou! Tech
            </Typography>
          </MotionBox>

          <MotionBox
            variants={fadeUp}
            sx={{
              mt: 3,
              display: "grid",
              gridTemplateColumns: {
                xs: "1fr",
                md: "minmax(0, 1.3fr) minmax(280px, 0.7fr)",
              },
              gap: {
                xs: 5,
                md: 8,
              },
              alignItems: "end",
            }}
          >
            <Typography
              component="h1"
              sx={{
                m: 0,
                fontFamily: "'Gilda Display', Georgia, serif",
                fontSize: {
                  xs: "3.5rem",
                  sm: "4.8rem",
                  md: "6.4rem",
                  lg: "7.5rem",
                },
                fontWeight: 500,
                lineHeight: 0.92,
                letterSpacing: "-0.055em",
                color: "#2B211C",
                maxWidth: 900,
              }}
            >
              Let's build
              <br />
              something
              <br />
              meaningful.
            </Typography>

            <Typography
              sx={{
                maxWidth: 390,
                pb: 1,
                fontSize: {
                  xs: "1rem",
                  md: "1.08rem",
                },
                lineHeight: 1.8,
                color: "#75675E",
              }}
            >
              Have an idea, a project, or simply a problem you want to solve?
              Tell us what you're working on. We'll help turn it into a clear,
              thoughtful digital experience.
            </Typography>
          </MotionBox>

          <MotionBox
            variants={fadeUp}
            sx={{
              mt: 6,
              display: "flex",
              flexWrap: "wrap",
              alignItems: "center",
              gap: 2,
            }}
          >
            <Button
              onClick={scrollToForm}
              endIcon={<ArrowDownwardRoundedIcon />}
              sx={{
                px: 3,
                py: 1.5,
                borderRadius: "999px",
                backgroundColor: "#3E2723",
                color: "#FFFDF8",
                textTransform: "none",
                fontWeight: 700,
                boxShadow: "none",
                "&:hover": {
                  backgroundColor: "#2B211C",
                  boxShadow: "none",
                },
              }}
            >
              Start a conversation
            </Button>

            <Typography
              sx={{
                fontSize: "0.82rem",
                color: "#75675E",
              }}
            >
              Usually responds within 1 business day.
            </Typography>
          </MotionBox>
        </MotionBox>

        {/* =====================================================
            CONTACT METHODS
        ====================================================== */}

        <MotionBox
          variants={stagger}
          initial="hidden"
          whileInView="visible"
          viewport={{
            once: true,
            amount: 0.2,
          }}
          sx={{
            maxWidth: 1180,
            mx: "auto",
            mb: {
              xs: 12,
              md: 16,
            },
          }}
        >
          <MotionBox variants={fadeUp}>
            <Divider
              sx={{
                borderColor: "#DCD4CC",
              }}
            />
          </MotionBox>

          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: {
                xs: "1fr",
                md: "repeat(3, 1fr)",
              },
            }}
          >
            {contactMethods.map((method, index) => (
              <MotionBox
                key={method.label}
                variants={fadeUp}
                component="a"
                href={method.href}
                target={
                  method.label === "WhatsApp" ? "_blank" : undefined
                }
                rel={
                  method.label === "WhatsApp"
                    ? "noopener noreferrer"
                    : undefined
                }
                sx={{
                  textDecoration: "none",
                  color: "inherit",
                  minHeight: {
                    xs: 150,
                    md: 190,
                  },
                  p: {
                    xs: 2.5,
                    md: 3,
                  },
                  borderBottom: {
                    xs: "1px solid #DCD4CC",
                    md: "none",
                  },
                  borderRight: {
                    md:
                      index < contactMethods.length - 1
                        ? "1px solid #DCD4CC"
                        : "none",
                  },
                  display: "flex",
                  flexDirection: "column",
                  justifyContent: "space-between",
                  transition:
                    "background-color 220ms ease, padding 220ms ease",
                  "&:hover": {
                    backgroundColor: "#FCFAF6",
                    px: {
                      md: 3.5,
                    },
                  },
                }}
              >
                <Box
                  sx={{
                    width: 42,
                    height: 42,
                    display: "grid",
                    placeItems: "center",
                    border: "1px solid #DCD4CC",
                    borderRadius: "50%",
                    color: "#3E2723",
                    backgroundColor: "#F7F4EF",
                  }}
                >
                  {React.cloneElement(method.icon, {
                    sx: {
                      fontSize: 19,
                    },
                  })}
                </Box>

                <Box>
                  <Typography
                    sx={{
                      mb: 0.5,
                      fontSize: "0.7rem",
                      fontWeight: 700,
                      letterSpacing: "0.14em",
                      textTransform: "uppercase",
                      color: "#A06E50",
                    }}
                  >
                    {method.label}
                  </Typography>

                  <Typography
                    sx={{
                      fontSize: {
                        xs: "1rem",
                        md: "1.05rem",
                      },
                      fontWeight: 600,
                      color: "#2B211C",
                    }}
                  >
                    {method.value}
                  </Typography>
                </Box>
              </MotionBox>
            ))}
          </Box>

          <MotionBox variants={fadeUp}>
            <Divider
              sx={{
                borderColor: "#DCD4CC",
              }}
            />
          </MotionBox>
        </MotionBox>

        {/* =====================================================
            FORM INTRO
        ====================================================== */}

        <MotionBox
          variants={stagger}
          initial="hidden"
          whileInView="visible"
          viewport={{
            once: true,
            amount: 0.15,
          }}
          sx={{
            maxWidth: 1180,
            mx: "auto",
            mb: 7,
          }}
        >
          <MotionBox variants={fadeUp}>
            <Typography
              sx={{
                mb: 1.5,
                fontSize: "0.72rem",
                fontWeight: 700,
                letterSpacing: "0.16em",
                textTransform: "uppercase",
                color: "#A06E50",
              }}
            >
              01 / Tell us about it
            </Typography>
          </MotionBox>

          <MotionBox
            variants={fadeUp}
            sx={{
              display: "grid",
              gridTemplateColumns: {
                xs: "1fr",
                md: "1fr 0.65fr",
              },
              gap: {
                xs: 3,
                md: 8,
              },
              alignItems: "end",
            }}
          >
            <Typography
              component="h2"
              sx={{
                m: 0,
                fontFamily: "'Gilda Display', Georgia, serif",
                fontSize: {
                  xs: "2.7rem",
                  md: "4.3rem",
                },
                lineHeight: 0.98,
                fontWeight: 500,
                letterSpacing: "-0.045em",
                color: "#2B211C",
              }}
            >
              A few details are
              <br />
              all we need to begin.
            </Typography>

            <Typography
              sx={{
                fontSize: "0.98rem",
                lineHeight: 1.8,
                color: "#75675E",
                maxWidth: 390,
              }}
            >
              Share your requirements, goals, or even a rough idea. Don't
              worry if everything isn't figured out yet — we'll help shape the
              direction with you.
            </Typography>
          </MotionBox>
        </MotionBox>

        {/* =====================================================
            CONTACT FORM
        ====================================================== */}

        <Box
          id="contact-form"
          sx={{
            maxWidth: 1180,
            mx: "auto",
            scrollMarginTop: "100px",
          }}
        >
          <ContactForm />
        </Box>

        {/* =====================================================
            AFTER FORM CTA
        ====================================================== */}

        <MotionBox
          variants={fadeUp}
          initial="hidden"
          whileInView="visible"
          viewport={{
            once: true,
            amount: 0.25,
          }}
          sx={{
            maxWidth: 1180,
            mx: "auto",
            mt: {
              xs: 12,
              md: 18,
            },
            pt: 7,
            borderTop: "1px solid #DCD4CC",
            display: "grid",
            gridTemplateColumns: {
              xs: "1fr",
              md: "1fr auto",
            },
            gap: 4,
            alignItems: "center",
          }}
        >
          <Box>
            <Typography
              sx={{
                mb: 1,
                fontFamily: "'Gilda Display', Georgia, serif",
                fontSize: {
                  xs: "2rem",
                  md: "2.8rem",
                },
                lineHeight: 1,
                letterSpacing: "-0.035em",
                color: "#2B211C",
              }}
            >
              Prefer a direct conversation?
            </Typography>

            <Typography
              sx={{
                color: "#75675E",
                fontSize: "0.95rem",
              }}
            >
              Call, WhatsApp, or email us — whichever feels easiest.
            </Typography>
          </Box>

          <Stack
            direction={{
              xs: "column",
              sm: "row",
            }}
            spacing={1.5}
          >
            <Button
              href={`tel:${PHONE_NUMBER.replace(/\s/g, "")}`}
              startIcon={<PhoneRoundedIcon />}
              sx={{
                minWidth: 150,
                px: 2.5,
                py: 1.3,
                borderRadius: "999px",
                border: "1px solid #DCD4CC",
                color: "#3E2723",
                backgroundColor: "#FCFAF6",
                textTransform: "none",
                fontWeight: 700,
                "&:hover": {
                  backgroundColor: "#F0EBE4",
                  borderColor: "#CFC4BA",
                },
              }}
            >
              Call us
            </Button>

            <Button
              href={`https://wa.me/${WHATSAPP_NUMBER}`}
              target="_blank"
              rel="noopener noreferrer"
              startIcon={<WhatsAppIcon />}
              endIcon={<ArrowOutwardRoundedIcon />}
              sx={{
                minWidth: 170,
                px: 2.5,
                py: 1.3,
                borderRadius: "999px",
                backgroundColor: "#3E2723",
                color: "#FFFDF8",
                textTransform: "none",
                fontWeight: 700,
                boxShadow: "none",
                "&:hover": {
                  backgroundColor: "#2B211C",
                  boxShadow: "none",
                },
              }}
            >
              WhatsApp
            </Button>
          </Stack>
        </MotionBox>

        {/* =====================================================
            RESPONSE NOTE
        ====================================================== */}

        <Box
          sx={{
            maxWidth: 1180,
            mx: "auto",
            mt: 8,
            display: "flex",
            alignItems: "center",
            gap: 1,
            color: "#75675E",
          }}
        >
          <AccessTimeRoundedIcon
            sx={{
              fontSize: 17,
            }}
          />

          <Typography
            sx={{
              fontSize: "0.78rem",
            }}
          >
            Your information is only used to respond to your enquiry.
          </Typography>
        </Box>
      </Container>
    </Box>
  );
};

export default Contact;